import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { Box, Button, Card, Container, Typography, Alert, Link } from "@mui/material";
import { CustomTextField } from "../../components/custom-textfield";
import { messages, SupportedLanguages } from "../../languages/languages";
import { FormattedMessage, IntlProvider } from "react-intl";
import { CampaignDoc, CandidateDoc } from "../../types/recruiting";
import { getServerTime } from "../../data-functions/common-api";
import {
  generateConfirmationCode,
  loadCampaign,
  loadCandidates,
  saveCandidate,
} from "../../data-functions/recruiting_api";
import { loadSponsor } from "../../data-functions/system-data_api";
import { SponsorDoc } from "../../types/sponsors";

type ParamTypes = {
  cid: string;
};

const ACTIVE_STEP_EMPTY = -1;
const ACTIVE_STEP_INPUT = 0;
const ACTIVE_STEP_SUCCESS = 1;
const ACTIVE_STEP_NOT_FOUND = 2;
const ACTIVE_STEP_INVALID_CAMPAIGN = 3;
const ACTIVE_STEP_EXPIRED_CAMPAIGN = 4;

export const VerifyCandidatePage: React.FC = () => {
  const { cid } = useParams<ParamTypes>();

  const [campaign, setCampaign] = useState<CampaignDoc | null>(null);
  const [sponsor, setSponsor] = useState<SponsorDoc | null>(null);
  const [language, setLanguage] = useState<SupportedLanguages>("en");
  const [activeStep, setActiveStep] = useState<number>(ACTIVE_STEP_EMPTY);

  const [email, setEmail] = useState<string>("");
  const [error, setError] = useState<string>("");

  useEffect(() => {
    const init = async () => {
      if (!cid) {
        setActiveStep(ACTIVE_STEP_INVALID_CAMPAIGN);
        return;
      }

      const campaignDoc = await loadCampaign(cid);
      if (!campaignDoc) {
        setActiveStep(ACTIVE_STEP_INVALID_CAMPAIGN);
        return;
      }

      setCampaign(campaignDoc);

      const sponsor = await loadSponsor(campaignDoc.campaign.sponsorId);
      if (sponsor) {
        setSponsor(sponsor);
      }

      if (campaignDoc.campaign.language && campaignDoc.campaign.language !== language) {
        setLanguage(campaignDoc.campaign.language as SupportedLanguages);
      }

      if (campaignDoc.campaign.status === "open") {
        setActiveStep(ACTIVE_STEP_INPUT);
      } else {
        setActiveStep(ACTIVE_STEP_EXPIRED_CAMPAIGN);
      }
    };

    init();
  }, [cid]);

  const handleCheckVerification = async () => {
    setError("");

    const emailValue = email.trim().toLowerCase();
    if (!emailValue || !cid) {
      setError("Please enter a valid email address");
      return;
    }

    try {
      const candidates = await loadCandidates(
        null,
        cid,
        { email: { value: emailValue, operator: "==" } },
        1
      );

      const entries = Object.entries(candidates);
      if (entries.length === 0) {
        setActiveStep(ACTIVE_STEP_NOT_FOUND);
        return;
      }

      const [candidateId, candidate] = entries[0];
      if (!candidate.verification) {
        candidate.verification = {
          verificationCode: "",
          expiryDate: new Date().toISOString(),
        };
        return;
      }

      const candidateDoc = {
        id: candidateId,
        candidate: candidate,
      };

      const serverTime = await getServerTime();
      const confirmationCode = await generateConfirmationCode(campaign?.id || "");
      if (!candidateDoc.candidate.verification) {
        candidateDoc.candidate.verification = {
          verificationCode: "",
          expiryDate: serverTime.iso,
        };
      }

      const updatedCandidate: CandidateDoc = {
        ...candidateDoc,
        candidate: {
          ...candidateDoc.candidate,
          verification: {
            ...candidateDoc.candidate.verification,
            verifiedOn: serverTime.iso,
            confirmationCode,
          },
          status: campaign?.campaign?.inviteAutomatically ? "invited" : "verified",
        },
      };

      await saveCandidate(null, updatedCandidate, false);

      setActiveStep(ACTIVE_STEP_SUCCESS);
    } catch (err) {
      console.error("Error verifying candidate:", err);
      setError("An error occurred. Please try again later.");
    }
  };

  const getInvalidCampaign = () => (
    <Box padding={"3rem"} paddingTop={"1rem"}>
      <FormattedMessage
        id="verify_campaign_invalid_title"
        defaultMessage="The verification link you have been provided is invalid!"
      />
    </Box>
  );

  const getExpiredCampaign = () => (
    <Box padding={"3rem"} paddingTop={"1rem"}>
      <Typography variant="h5" color="error" sx={{ marginBottom: "1rem", textAlign: "center" }}>
        <FormattedMessage
          id="verify_campaign_expired_title"
          defaultMessage="This campaign has alread expired and does not accept registrations anymore!"
        />{" "}
      </Typography>
    </Box>
  );

  const getInput = () => (
    <Box
      sx={{
        padding: {
          xs: "1rem",
          md: "3rem",
        },
        paddingTop: "1rem",
      }}
    >
      <Typography variant="h5" sx={{ marginBottom: "1rem", textAlign: "center" }}>
        <FormattedMessage
          id="register_welcome_title"
          defaultMessage="Working in the United Kingdom with a visa"
        />
      </Typography>
      <Typography>
        <FormattedMessage
          id="verify_welcome_hint1"
          defaultMessage="Please enter your email address to check your reigstration verifiction."
        />
      </Typography>

      <Box display="flex" flexDirection="column" gap={2}>
        {error && <Alert severity="error">{error}</Alert>}
        <CustomTextField
          name="email"
          label="Email Address"
          type="email"
          fullWidth
          value={email}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
            setEmail(e.target.value?.toLowerCase() || "")
          }
        />
        <Box display="flex" justifyContent="center" alignItems="center">
          <Button variant="contained" onClick={handleCheckVerification}>
            <FormattedMessage
              id="button_verify_registration"
              defaultMessage={"Verify Registration"}
            />
          </Button>
        </Box>
      </Box>
    </Box>
  );

  const getSuccess = () => (
    <Box padding={"3rem"} paddingTop={"1rem"}>
      <Typography variant="h5" sx={{ marginBottom: "1rem", textAlign: "center" }}>
        <FormattedMessage
          id="verify_registration_confirmed_title"
          defaultMessage="We want to inform you that your registration has been successful."
        />
      </Typography>
      <Typography sx={{ marginBottom: "1rem", textAlign: "left" }}>
        <FormattedMessage
          id="verify_registration_confirmed_hint1"
          defaultMessage="If you are suitable for us, we will send you further instructions to your email address."
        />
      </Typography>
      <Typography sx={{ marginBottom: "1rem", textAlign: "left" }}>
        <FormattedMessage
          id="verify_registration_confirmed_hint2"
          defaultMessage="Thank you for cooperation!"
        />
      </Typography>
      <Typography sx={{ textAlign: "left" }}>
        <FormattedMessage
          id="verify_registration_confirmed_hint3"
          defaultMessage="Best regards,"
          values={{ sponsor: sponsor?.sponsor.name }}
        />
      </Typography>
      {sponsor?.sponsor.name && (
        <Typography sx={{ marginBottom: "1rem", textAlign: "left" }}>
          {sponsor?.sponsor.name}
        </Typography>
      )}
    </Box>
  );

  const getNotFound = () => (
    <Box padding={"3rem"} paddingTop={"1rem"}>
      <Typography variant="h6" sx={{ marginBottom: "0.5rem", textAlign: "center" }}>
        <FormattedMessage
          id="verify_registration_not_found_title"
          defaultMessage="Please complete the registration again using this link:"
        />
      </Typography>
      {campaign?.id && (
        <Box display="flex" justifyContent="center" alignItems="center">
          <Link href={"https://visauk.realizon.tech/register/" + campaign.id}>
            {"https://visauk.realizon.tech/register/" + campaign.id}
          </Link>
        </Box>
      )}
    </Box>
  );

  const getActivePage = () => {
    switch (activeStep) {
      case ACTIVE_STEP_INPUT:
        return getInput();
      case ACTIVE_STEP_SUCCESS:
        return getSuccess();
      case ACTIVE_STEP_NOT_FOUND:
        return getNotFound();
      case ACTIVE_STEP_INVALID_CAMPAIGN:
        return getInvalidCampaign();
      case ACTIVE_STEP_EXPIRED_CAMPAIGN:
        return getExpiredCampaign();
      default:
        return <Box padding={"3rem"} paddingTop={"1rem"} />;
    }
  };

  return (
    <IntlProvider locale={language} messages={messages[language]}>
      <Container maxWidth="md" sx={{ marginTop: "2rem", marginBottom: "2rem" }}>
        <Box display="flex" justifyContent="center" alignItems="center">
          {sponsor && sponsor.sponsor.logo && (
            <img
              src={sponsor.sponsor.logo}
              alt="operatorlogo"
              style={{ maxHeight: "200px", width: "auto" }}
            />
          )}
        </Box>
        <Card sx={{ marginTop: "1rem", padding: "1rem" }}>{getActivePage()}</Card>
      </Container>
    </IntlProvider>
  );
};
